import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';

type OnlineStatusContextType = {
  onlineStatus: boolean;
  updateWaiting: boolean;
  handleUpdate: () => void;
};

const OnlineStatusContext = createContext<OnlineStatusContextType | null>(null);

export const OnlineStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [onlineStatus, setOnlineStatus] = useState(true);
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const handleUpdate = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnlineStatus(true);
    const handleOffline = () => setOnlineStatus(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleUpdateFound = () => {
      const sw = swRegistrationRef.current;
      if (!sw) return;
      const newWorker = sw.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          setUpdateWaiting(true);
        }
      });
    };

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) return;
      swRegistrationRef.current = registration;
      registration.addEventListener('updatefound', handleUpdateFound);
      registration.update();
    });

    return () => {
      swRegistrationRef.current?.removeEventListener('updatefound', handleUpdateFound);
    };
  }, []);

  const value = useMemo(
    () => ({ onlineStatus, updateWaiting, handleUpdate }),
    [onlineStatus, updateWaiting, handleUpdate],
  );

  return (
    <OnlineStatusContext.Provider value={value}>
      {children}
    </OnlineStatusContext.Provider>
  );
};

export const useOnlineStatus = () => {
  const context = useContext(OnlineStatusContext);
  if (!context) {
    throw new Error(
      'useOnlineStatus must be used within an OnlineStatusProvider',
    );
  }
  return context;
};
